<template>
  <div v-if="views.length" class="source-view-links" :class="{ open, 'is-anchored': anchored }" @click.stop @keydown.stop>
    <button
      ref="trigger"
      type="button"
      class="source-view-trigger"
      :aria-expanded="open.toString()"
      @click="open = !open"
      :aria-controls="panelId"
    >
      <span ref="label" class="source-view-label">
        <span class="source-view-star" aria-hidden="true">✦</span>
        Seen in {{ views.length }} {{ views.length === 1 ? 'View' : 'Views' }}
      </span>
      <span class="source-view-arrow" aria-hidden="true">›</span>
    </button>
    <div :id="panelId" ref="reveal" class="source-view-reveal" :class="{ open }" :aria-hidden="(!open).toString()" :inert="open ? null : ''">
      <div class="source-view-clip">
      <ul class="source-view-list">
        <li v-for="view in views" :key="view.id">
          <router-link :to="`/view/${view.id}`">
            <span>{{ view.title }}</span>
            <time :datetime="new Date(Number(view.created_at)).toISOString()">{{ formatDate(view.created_at) }}</time>
          </router-link>
        </li>
      </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { listViewsBySource } from '../../utils/viewShareSite';

export default {
  name: 'SourceViewLinks',
  props: {
    sourceId: {
      type: String,
      required: true,
    },
    anchored: Boolean,
    aliases: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      open: false,
      views: [],
    };
  },
  computed: { panelId() { return `source-view-links-${this.$.uid}`; } },
  mounted() { this.observeReveal(); },
  beforeUnmount() { this._revealObserver?.disconnect(); },
  emits: ['reveal-bottom'],
  watch: {
    views() { this.$nextTick(() => this.observeReveal()); },
    aliases: { deep: true, handler() { this.loadViews(); } },
    sourceId: {
      immediate: true,
      handler() {
        this.loadViews();
      },
    },
  },
  methods: {
    observeReveal() {
      this._revealObserver?.disconnect();
      if (this.anchored && this.$refs.reveal) {
        this._revealObserver = new ResizeObserver(() => {
          const rect = this.$refs.reveal.getBoundingClientRect();
          this.$emit('reveal-bottom', rect.height > 1 ? rect.bottom : null);
        });
        this._revealObserver.observe(this.$refs.reveal);
      }
    },
    formatDate(value) {
      const date = new Date(Number(value));
      if (Number.isNaN(date.getTime())) return '';
      return new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
    },
    async loadViews() {
      if (!this.sourceId) {
        this.views = [];
        return;
      }
      try {
        const index = await listViewsBySource();
        const ids = [this.sourceId, ...this.aliases];
        const unique = new Map();
        for (const id of ids) {
          for (const view of index[id] || []) unique.set(view.id, view);
        }
        this.views = [...unique.values()].sort((a, b) => Number(b.created_at) - Number(a.created_at));
      } catch {
        this.views = [];
      }
    },
  },
};
</script>

<style scoped>
.source-view-links {
  max-width: 38rem;
  margin-top: 1.05rem;
  color: #777;
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.78rem;
  line-height: 1.45;
}

.source-view-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.48rem;
  position: relative;
  width: 100%;
  min-height: 2rem;
  margin: 0;
  padding: 0.26rem 0;
  border: 0;
  background: transparent;
  color: inherit;
  cursor: var(--site-interactive-cursor);
  font: inherit;
  transition: color 140ms ease;
}

.source-view-trigger:hover {
  color: #333;
}

.source-view-trigger:active { color: var(--site-text-primary); }

.source-view-trigger:focus-visible {
  outline: 2px solid rgba(var(--site-accent-rgb), 0.65);
  outline-offset: 3px;
}

.source-view-label {
  display: inline-flex;
  align-items: baseline;
  gap: 0.34rem;
  transition: transform 260ms cubic-bezier(.23, 1, .32, 1);
}
.source-view-links.is-anchored { position: relative; }
.is-anchored .source-view-reveal { position: absolute; top: 100%; left: 0; width: 100%; }
.source-view-reveal { display: grid; grid-template-rows: 0fr; transition: grid-template-rows 260ms cubic-bezier(.23, 1, .32, 1); }
.source-view-reveal.open { grid-template-rows: 1fr; }
.source-view-clip { min-height: 0; overflow: hidden; }
.source-view-reveal .source-view-list { opacity: 0; transform: translateY(-5px); transition: opacity 260ms ease, transform 260ms cubic-bezier(.23, 1, .32, 1); }
.source-view-reveal.open .source-view-list { opacity: 1; transform: none; }

.source-view-star {
  color: var(--site-accent);
  font-size: 0.92rem;
  line-height: 1;
}

.source-view-arrow {
  color: #a0a0a0;
  font-size: 1rem;
  line-height: 1;
  transition: transform 180ms cubic-bezier(0.22, 1, 0.36, 1), color 140ms ease;
}

.source-view-links.open .source-view-arrow {
  transform: rotate(90deg);
}

.source-view-list {
  display: grid;
  gap: 0;
  margin: 0.38rem 0 0 1.28rem;
  padding: 0;
  list-style: none;
}

.source-view-list li {
  margin: 0;
}

.source-view-list a {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.34rem 0;
  color: #777;
  text-decoration: none;
  transition: color 140ms ease;
}

.source-view-list time {
  flex: 0 0 auto;
  color: #a0a0a0;
  font-size: 0.68rem;
  letter-spacing: 0.02em;
}

.source-view-list a:hover {
  color: #222;
}

html.dark-mode .source-view-links,
body.dark-mode .source-view-links,
html.dark-mode .source-view-list a,
body.dark-mode .source-view-list a {
  color: var(--site-text-tertiary);
}

html.dark-mode .source-view-list time,
body.dark-mode .source-view-list time {
  color: var(--site-text-tertiary);
}

html.dark-mode .source-view-trigger:hover,
body.dark-mode .source-view-trigger:hover,
html.dark-mode .source-view-list a:hover,
body.dark-mode .source-view-list a:hover {
  color: #f4f1ec;
}

html.dark-mode .source-view-trigger:hover .source-view-arrow,
body.dark-mode .source-view-trigger:hover .source-view-arrow {
  color: var(--site-accent);
}

html.dark-mode .source-view-star,
body.dark-mode .source-view-star {
  color: var(--site-accent);
}

@media (prefers-reduced-motion: reduce) {
  .source-view-trigger,
  .source-view-label,
  .source-view-reveal,
  .source-view-arrow,
  .source-view-list,
  .source-view-list a {
    transition-duration: 0.01ms;
  }
}
</style>
